import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { finalize, switchMap, takeUntil, tap } from 'rxjs/operators';
import { CompanyModel } from '@data-layer/models/companies';
import { UntypedFormControl } from '@angular/forms';
import { CustomValidator } from '@helpers/custom-validator';
import { ALLOWED_FILE_EXTENSIONS, ALLOWED_FILE_MIMES } from '@constants/constants';
import { environment } from '@env/environment';
import { DocumentCreateModel, DocumentModel } from '@data-layer/models/documents';
import { Observable, ReplaySubject } from 'rxjs';
import { DocumentsService } from '@services/documents.service';
import { IsfModel } from '@data-layer/models/isf';
import {
  enumDocumentRelatedEntityType,
  enumDocumentRootEntityType,
  enumDocumentState,
  enumDocumentType,
} from '@data-layer/enums/documents';
import { AlertService } from '@services/alert.service';
import { TrackByService } from '@app/shared/services';

@Component({
  selector: 'desktop-summary-upload-isf-documents',
  template: `
    <isf-data-card
      cardTitle="LABELS.ADD_DOCUMENTS"
      imgSrc="assets/icons/vessel.svg"
      [isWithHeaderActions]="true"
      [isWithIcon]="false"
      [dataIsValid]="uploadedDocsLength > 0"
    >
      <ng-container header>
        <div class="d-flex align-items-center">
          <div class="download-all-files" *ngIf="isUploadedAnyFiles && !isHideDownloadAll" (click)="onDownloadAllFiles()">
            {{ 'LABELS.DOWNLOAD_ALL_FILES' | translate }}
          </div>

          <div class="btn-group add-documents d-print-none" dropdown *ngIf="isEditMode">
            <button id="button-files" dropdownToggle type="button" class="btn btn-link dropdown-toggle">
              <span class="wrapper-icon-add"><i class="fa fa-plus"></i></span>
              <span>{{ 'BUTTON.ADD_OTHER_DOCUMENT' | translate }}</span>
              <span class="wrapper-icon-carret"><i class="fa fa-caret-down"></i></span>
            </button>
            <ul id="dropdown-files" class="dropdown-menu" role="menu" *dropdownMenu>
              <li role="menuitem" *ngFor="let doc of availableDocTypes" (click)="onAddOtherDocument(doc)">
                <span class="dropdown-item">{{ 'DOCUMENT_TYPE.' + enumDocumentType[doc] | translate }}</span>
              </li>
            </ul>
          </div>
        </div>
      </ng-container>

      <ng-container body>
        <div class="document-upload-isf-wrapper">
          <span class="document-upload-isf-hint">{{ 'LABELS.ADD_DOCUMENTS_ISF_HINT' | translate }}</span>

          <div class="d-flex flex-wrap">
            <div class="document-upload-isf-item" *ngFor="let document of attachedDocuments; let index = index; trackBy: trackByFn">
              <document-upload-desktop
                [id]="'isf-documents-' + isf.id + '-' + index + (document.id ? '-' + document.id : '')"
                [attachmentName]="'DOCUMENT_TYPE.' + enumDocumentType[document.type]"
                [attachmentTypeName]="'LABELS.ISF' | translate | uppercase"
                [attachmentHint]="getAttachmentHint(document)"
                [uploadedDocument]="document"
                [canUploadDocuments]="isEditMode"
                [canRemoveDocuments]="isCanDeleteDoc(document.type)"
                [isStretched]="true"
                [isRequired]="isRequiredDoc(document.type)"
                (upload)="uploadDocumentCall(document, $event)"
                (remove)="removeDocument(document.id)"
                (stateUpdate)="onDocumentStateUpdate(document.id, $event)"
              ></document-upload-desktop>
            </div>
          </div>
        </div>

        <loading-spinner [show]="isLoading"></loading-spinner>
      </ng-container>
    </isf-data-card>

    <input class="d-none" type="file" #input [accept]="ALLOWED_FILE_MIMES.join(',')" (change)="handleFiles($event)" />
  `,
  styleUrls: ['./desktop-summary-upload-isf-documents.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DesktopSummaryUploadIsfDocumentsComponent implements OnInit, OnDestroy {
  @Input() public company: CompanyModel;
  @Input() public isf: IsfModel;
  @Input() public isEditMode: boolean = true;
  @Input() public isHideDelete: boolean;
  @Input() public isHideDownloadAll: boolean;
  @Output() public downloadAllFiles: EventEmitter<number[]> = new EventEmitter<number[]>();

  @ViewChild('input', { static: true }) public inputRef: ElementRef;

  public enumDocumentType: typeof enumDocumentType = enumDocumentType;
  public ALLOWED_FILE_MIMES: typeof ALLOWED_FILE_MIMES = ALLOWED_FILE_MIMES;

  public availableDocTypes: enumDocumentType[];
  public isLoading: boolean;
  public fileControl: UntypedFormControl = new UntypedFormControl(null, [
    CustomValidator.fileExtensions(ALLOWED_FILE_EXTENSIONS),
    CustomValidator.maxFileSize(environment.files.default_file_size_limit),
  ]);
  public attachedDocuments: DocumentModel[] = [];
  public currentClickedType: enumDocumentType;

  public trackByFn: (item: any) => string | number | string = TrackByService.trackById;

  private destroy$: ReplaySubject<boolean> = new ReplaySubject<boolean>();

  constructor(private changeDetector: ChangeDetectorRef, private documentsService: DocumentsService, private alertService: AlertService) {}

  public ngOnInit(): void {
    this.loadDocuments(true).pipe(takeUntil(this.destroy$)).subscribe();

    this.availableDocTypes = this.initDocumentTypes();
  }

  public ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  public onAddOtherDocument(type: enumDocumentType): void {
    this.currentClickedType = type;
    this.inputRef.nativeElement.click();
  }

  public handleFiles(event: Event): void {
    const target: HTMLInputElement = event.target as HTMLInputElement;
    const files: FileList = target.files;
    if (files && files.length) {
      this.isLoading = true;
      this.uploadFile(files[0], (file: File) => {
        this.uploadDocument(
          {
            relatedEntityId: this.isf.id,
            relatedEntityType: enumDocumentRelatedEntityType.ISF,
            rootEntityType: enumDocumentRootEntityType.ISF,
            rootEntityId: this.isf.id,
            type: this.currentClickedType,
            fileName: file.name,
          },
          file,
        )
          .pipe(
            takeUntil(this.destroy$),
            finalize(() => (this.isLoading = false)),
          )
          .subscribe();
      });
    }
    this.inputRef.nativeElement.value = '';
  }

  public onDocumentStateUpdate(documentId: number, state: enumDocumentState): void {
    const typesToSkip: enumDocumentType[] = [enumDocumentType.FILE_PREVIEW];
    this.attachedDocuments = this.attachedDocuments
      .filter((doc) => !typesToSkip.includes(doc.type))
      .map((document: DocumentModel) => (document.id === documentId ? { ...document, state } : document));
    this.changeDetector.markForCheck();
  }

  public uploadDocument(document: DocumentCreateModel, file: File): Observable<any> {
    return this.documentsService.uploadDocument(document, file, this.company.id).pipe(switchMap(() => this.loadDocuments(false)));
  }

  public uploadDocumentCall(document: DocumentCreateModel, file: File): void {
    this.uploadDocument(document, file).pipe(takeUntil(this.destroy$)).subscribe();
  }

  public removeDocument(documentId: number): void {
    this.documentsService
      .removeDocument(documentId, true)
      .pipe(
        switchMap(() => this.loadDocuments(false)),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  public getAttachmentHint(document: DocumentModel): string {
    if (document.type === enumDocumentType.ARRIVAL_NOTICE) {
      return 'LABELS.ARRIVAL_NOTICE_HINT';
    } else if (document.type === enumDocumentType.ISF_FORM) {
      return 'LABELS.ISF_FORM_HINT';
    } else if (document.type === enumDocumentType.BILL_OF_LADING) {
      return 'LABELS.BILL_OF_LADING_HINT';
    }

    return null;
  }

  public get uploadedDocsLength(): number {
    return this.attachedDocuments.filter((doc: DocumentModel) => doc.state === enumDocumentState.UPLOADED).length;
  }

  public isCanDeleteDoc(docType: enumDocumentType): boolean {
    if (this.isHideDelete) {
      return false;
    }

    if ([enumDocumentType.FINAL_STATEMENT, enumDocumentType.PRELIMINARY_STATEMENT, enumDocumentType.ISF_STATUS_SHEET].includes(docType)) {
      return false;
    }

    return this.isEditMode;
  }

  public isRequiredDoc(docType: enumDocumentType): boolean {
    return [enumDocumentType.ISF_STATUS_SHEET].includes(docType);
  }

  public onDownloadAllFiles(): void {
    this.downloadAllFiles.emit(this.attachedDocuments.filter((item: DocumentModel) => !!item.id).map((item: DocumentModel) => item.id));
  }

  public get isUploadedAnyFiles(): boolean {
    return !!this.attachedDocuments.filter((item: DocumentModel) => !!item.id).length;
  }

  private initDocumentTypes(): enumDocumentType[] {
    return [enumDocumentType.ARRIVAL_NOTICE, enumDocumentType.ISF_FORM, enumDocumentType.BILL_OF_LADING, enumDocumentType.UNKNOWN];
  }

  private uploadFile(file: File, callBack: (file: File) => void): void {
    this.clearFile();
    this.fileControl.patchValue(file);
    this.fileControl.markAsTouched();
    this.fileControl.valid
      ? callBack(file)
      : this.alertService.error(`Please upload files up to ${environment.files.default_file_size_limit}MB only.`);
    this.inputRef.nativeElement.value = null;
  }

  private clearFile(): void {
    this.fileControl.patchValue(null);
    this.fileControl.markAsUntouched();
  }

  private loadDocuments(isShowLoader: boolean): Observable<DocumentModel[]> {
    return this.documentsService.getIsfDocuments(this.isf.id, isShowLoader).pipe(
      takeUntil(this.destroy$),
      tap((documents: DocumentModel[]) => {
        const typesToSkip: enumDocumentType[] = [enumDocumentType.FILE_PREVIEW];
        this.attachedDocuments = [...documents.filter((doc) => !typesToSkip.includes(doc.type))];

        if (!documents.find((document: DocumentModel) => document.type === enumDocumentType.ISF_FORM)) {
          this.attachedDocuments.unshift({
            relatedEntityId: this.isf.id,
            type: enumDocumentType.ISF_FORM,
            relatedEntityType: enumDocumentRelatedEntityType.ISF,
            rootEntityType: enumDocumentRootEntityType.ISF,
            rootEntityId: this.isf.id,
            url: null,
            createdDate: null,
            id: null,
            fileName: null,
            state: enumDocumentState.CREATED,
          });
        }

        if (!documents.find((document: DocumentModel) => document.type === enumDocumentType.BILL_OF_LADING)) {
          this.attachedDocuments.unshift({
            relatedEntityId: this.isf.id,
            type: enumDocumentType.BILL_OF_LADING,
            relatedEntityType: enumDocumentRelatedEntityType.ISF,
            rootEntityType: enumDocumentRootEntityType.ISF,
            rootEntityId: this.isf.id,
            url: null,
            createdDate: null,
            id: null,
            fileName: null,
            state: enumDocumentState.CREATED,
          });
        }

        this.changeDetector.markForCheck();
      }),
    );
  }
}
